import { SyntheticEvent, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import { Product } from '@prisma/client';

import { RadioGroupBase } from 'src/components/common/form/FormRadioGroup';
import NPICurrencyInput from 'src/components/inputs/NPICurrencyInput/NPICurrencyInput';
import ProductLimit from 'src/components/ProductLimit';
import { useAuthContext } from 'src/contexts/AuthProvider';
import {
    PricingLimit,
    SalesConditionsType,
    SelectedProducts,
    pricingTypes,
} from 'src/hooks/usePricings';

interface PricingAccordionProps {
    products: SelectedProducts[];
    handleChange: (panel: string) => (event: SyntheticEvent, isExpanded: boolean) => void;
    expanded: string | false;
    handlePricingTypeOnChange: (value: string, id: number) => void;
    handleMultiplePricingOnChange: (p: Product) => void;
    handleApplyPromo: (value: string, id: number) => void;
    styling: string;
    handlePricingLimitChange: (limit: string, value: number, id: number) => void;
}

const PricingAccordion = ({
    products,
    handleChange,
    expanded,
    handlePricingTypeOnChange,
    handleMultiplePricingOnChange,
    handleApplyPromo,
    styling,
    handlePricingLimitChange,
}: PricingAccordionProps) => {
    const { isPartner } = useAuthContext();
    const [prevValue, setPrevValue] = useState(0);

    return (
        <Box>
            {products.map((product: SelectedProducts, index: number) => {
                return (
                    <Accordion
                        key={index}
                        className={`${styling}-accordion`}
                        expanded={expanded === `panel${product.id}`}
                        onChange={handleChange(`panel${product.id}`)}
                    >
                        <AccordionSummary
                            className={`${styling}-accordion-summary accordion-summary`}
                            expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}
                            aria-controls={`panel${product?.id}bh-content`}
                            id={`panel${product?.id}bh-header`}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>
                                    {product.name}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails
                            className={`${styling}-accordion-details accordion-details`}
                        >
                            <Box>
                                <Grid container spacing={5}>
                                    <Grid item md={2}>
                                        <Box sx={{ width: '160px', marginTop: '40px' }}>
                                            <img
                                                src={product.imageUrl}
                                                width="160px"
                                                alt="product-image"
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item md={10}>
                                        <Box className="plain-bottom-border">
                                            <Grid
                                                container
                                                spacing={3}
                                                sx={{ marginBottom: '20px' }}
                                            >
                                                <Grid item md={6}>
                                                    <Typography variant="h3">
                                                        Pricing Type
                                                    </Typography>
                                                    <Box sx={{ marginLeft: '-10px' }}>
                                                        <RadioGroupBase
                                                            containerGridSpacing={3}
                                                            labelGridSize={0}
                                                            radioGroupGridSize={10}
                                                            radioOptionGridSize={4}
                                                            defaultValue={product?.pricing?.type}
                                                            setselectedvalue={(value: string) => {
                                                                return handlePricingTypeOnChange(
                                                                    value,
                                                                    product.id
                                                                );
                                                            }}
                                                            name="type"
                                                            disabledOptions={
                                                                isPartner ? [1, 2, 3] : []
                                                            }
                                                            options={[
                                                                {
                                                                    label: pricingTypes.ONE_OFF,
                                                                    value: pricingTypes.ONE_OFF,
                                                                },
                                                                {
                                                                    label: pricingTypes.MONTHLY,
                                                                    value: pricingTypes.MONTHLY,
                                                                },
                                                                {
                                                                    label: pricingTypes.BOTH,
                                                                    value: pricingTypes.BOTH,
                                                                },
                                                            ]}
                                                        />
                                                    </Box>
                                                    <Stack p={1} />
                                                    <Typography variant="h3">
                                                        Apply Promo?
                                                    </Typography>
                                                    <Box sx={{ marginLeft: '-10px' }}>
                                                        <RadioGroupBase
                                                            containerGridSpacing={3}
                                                            labelGridSize={0}
                                                            radioGroupGridSize={10}
                                                            radioOptionGridSize={4}
                                                            defaultValue={product.shouldApplyPromo}
                                                            setselectedvalue={(value: string) => {
                                                                handleApplyPromo(value, product.id);
                                                            }}
                                                            name="shouldApplyPromo"
                                                            disabledOptions={
                                                                isPartner ? [1, 2] : []
                                                            }
                                                            options={[
                                                                {
                                                                    label: 'Yes',
                                                                    value: SalesConditionsType.YES,
                                                                },
                                                                {
                                                                    label: 'No',
                                                                    value: SalesConditionsType.NO,
                                                                },
                                                            ]}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item md={6}>
                                                    <Stack spacing={3}>
                                                        {/* One-off Limit Section */}
                                                        <Typography variant="h3">
                                                            One-off Limit
                                                        </Typography>
                                                        <Stack
                                                            flexDirection="row"
                                                            alignItems={'center'}
                                                            gap={2}
                                                        >
                                                            <Stack width="50%" spacing={1}>
                                                                <Typography>Lowest</Typography>

                                                                <NPICurrencyInput
                                                                    name="one-off-limit-lowest"
                                                                    defaultValueInCents={
                                                                        product.pricing.oneOff
                                                                            .lowest
                                                                    }
                                                                    disabled={
                                                                        product?.pricing?.type ===
                                                                        pricingTypes.MONTHLY
                                                                    }
                                                                    handleValue={(valueInCents) => {
                                                                        if (
                                                                            product.pricing.oneOff
                                                                                .lowest !==
                                                                            valueInCents
                                                                        ) {
                                                                            handlePricingLimitChange(
                                                                                PricingLimit.oneOff
                                                                                    .lowest,
                                                                                valueInCents,
                                                                                product.id
                                                                            );
                                                                        }
                                                                    }}
                                                                    error={
                                                                        product.pricing.oneOff
                                                                            .highest <
                                                                        product.pricing.oneOff
                                                                            .lowest
                                                                    }
                                                                />
                                                                {product.pricing.oneOff.highest <
                                                                    product.pricing.oneOff
                                                                        .lowest && (
                                                                    <Typography
                                                                        color="error"
                                                                        variant="body2"
                                                                    >
                                                                        Lowest limit cannot exceed
                                                                        highest limit.
                                                                    </Typography>
                                                                )}
                                                            </Stack>

                                                            <Stack width="50%" spacing={1}>
                                                                <Typography>Highest</Typography>
                                                                <NPICurrencyInput
                                                                    name="one-off-limit-highest"
                                                                    defaultValueInCents={
                                                                        product.pricing.oneOff
                                                                            .highest
                                                                    }
                                                                    disabled={
                                                                        product?.pricing?.type ===
                                                                        pricingTypes.MONTHLY
                                                                    }
                                                                    handleValue={(valueInCents) => {
                                                                        if (
                                                                            product.pricing.oneOff
                                                                                .highest !==
                                                                            valueInCents
                                                                        ) {
                                                                            handlePricingLimitChange(
                                                                                PricingLimit.oneOff
                                                                                    .highest,
                                                                                valueInCents,
                                                                                product.id
                                                                            );
                                                                        }
                                                                    }}
                                                                    error={
                                                                        product.pricing.oneOff
                                                                            .highest <
                                                                        product.pricing.oneOff
                                                                            .lowest
                                                                    }
                                                                />
                                                                {product.pricing.oneOff.highest <
                                                                    product.pricing.oneOff
                                                                        .lowest && (
                                                                    <Typography
                                                                        color="error"
                                                                        variant="body2"
                                                                    >
                                                                        Highest limit cannot be less
                                                                        than lowest limit.
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </Stack>

                                                        {/* Monthly Limit Section */}
                                                        <Typography variant="h3">
                                                            Monthly Limit
                                                        </Typography>
                                                        <Stack spacing={2} direction="row">
                                                            <Stack width="50%" spacing={1}>
                                                                <Typography>Lowest</Typography>
                                                                <NPICurrencyInput
                                                                    name="monthly-limit-lowest"
                                                                    defaultValueInCents={
                                                                        product.pricing.monthly
                                                                            .lowest
                                                                    }
                                                                    disabled={
                                                                        product?.pricing?.type ===
                                                                        pricingTypes.ONE_OFF
                                                                    }
                                                                    handleValue={(valueInCents) => {
                                                                        if (
                                                                            product.pricing.monthly
                                                                                .lowest !==
                                                                            valueInCents
                                                                        ) {
                                                                            handlePricingLimitChange(
                                                                                PricingLimit.monthly
                                                                                    .lowest,
                                                                                valueInCents,
                                                                                product.id
                                                                            );
                                                                        }
                                                                    }}
                                                                    error={
                                                                        product.pricing.monthly
                                                                            .highest <
                                                                        product.pricing.monthly
                                                                            .lowest
                                                                    }
                                                                />
                                                                {product.pricing.monthly.highest <
                                                                    product.pricing.monthly
                                                                        .lowest && (
                                                                    <Typography
                                                                        color="error"
                                                                        variant="body2"
                                                                    >
                                                                        Lowest limit cannot exceed
                                                                        highest limit.
                                                                    </Typography>
                                                                )}
                                                            </Stack>

                                                            <Stack width="50%" spacing={1}>
                                                                <Typography>Highest</Typography>
                                                                <NPICurrencyInput
                                                                    name="monthly-limit-highest"
                                                                    defaultValueInCents={
                                                                        product.pricing.monthly
                                                                            .highest
                                                                    }
                                                                    disabled={
                                                                        product?.pricing?.type ===
                                                                        pricingTypes.ONE_OFF
                                                                    }
                                                                    handleValue={(valueInCents) => {
                                                                        if (
                                                                            prevValue !==
                                                                            valueInCents
                                                                        ) {
                                                                            setPrevValue(
                                                                                valueInCents
                                                                            );
                                                                            handlePricingLimitChange(
                                                                                PricingLimit.monthly
                                                                                    .highest,
                                                                                valueInCents,
                                                                                product.id
                                                                            );
                                                                        }
                                                                    }}
                                                                    error={
                                                                        product.pricing.monthly
                                                                            .highest <
                                                                        product.pricing.monthly
                                                                            .lowest
                                                                    }
                                                                />
                                                                {product.pricing.monthly.highest <
                                                                    product.pricing.monthly
                                                                        .lowest && (
                                                                    <Typography
                                                                        color="error"
                                                                        variant="body2"
                                                                    >
                                                                        Highest limit cannot be less
                                                                        than lowest limit.
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </Stack>
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        <Box sx={{ marginTop: '20px' }}>
                                            <Grid container spacing={5}>
                                                <Grid item md={6}>
                                                    <Box>
                                                        <ProductLimit
                                                            name="oneOff"
                                                            title="One-Off Limit"
                                                            isAddRowButtonDisabled={
                                                                product?.pricing?.type ===
                                                                pricingTypes.MONTHLY
                                                            }
                                                            product={product}
                                                            onUpdateProduct={(
                                                                updatedProduct: Product
                                                            ) => {
                                                                return handleMultiplePricingOnChange(
                                                                    updatedProduct
                                                                );
                                                            }}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item md={6}>
                                                    <Box>
                                                        <ProductLimit
                                                            name="monthly"
                                                            title="Monthly Limit"
                                                            isAddRowButtonDisabled={
                                                                product?.pricing?.type ===
                                                                pricingTypes.ONE_OFF
                                                            }
                                                            product={product}
                                                            onUpdateProduct={(
                                                                updatedProduct: Product
                                                            ) => {
                                                                return handleMultiplePricingOnChange(
                                                                    updatedProduct
                                                                );
                                                            }}
                                                        />
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};

export default PricingAccordion;
